import DocFileIcon from '@components/DocFileIcon/DocFileIcon';
import FormikDatePicker from '@components/FormFields/FormikDatePicker';
import RequiredMark from '@components/FormFields/RequiredMark';
import { Button, Card, CircularProgress, HelpText, Label } from '@hyperflake/react-ui-library';
import { DeleteIcon, PlusIcon } from '@icon/index';
import { FormHelperText } from '@library/forms';
import { getFileExtension } from '@utils/file-utils';
import { ErrorMessage, Form, Formik, FieldArray } from 'formik';
import { useBusinessDevelopmentListData } from 'modules/client-installation/hooks/useBusinessDevelopmentListData';
import useClientInstallationAddForm from 'modules/client-installation/hooks/useClientInstallationAddForm';
import { PageHeading, PageTemplate } from 'modules/shared/features/content';
import { FormikInput, FormikSelect } from 'modules/shared/features/formik';
import { countryOptions } from 'modules/shared/helpers/shared.constants';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const AddClientInstallationForm = () => {
    const formHook = useClientInstallationAddForm();
    const { businessDevelopmentUser, isLoading } = useBusinessDevelopmentListData();
    const navigate = useNavigate();

    const BdUserOption = useMemo(() => {
        if (!businessDevelopmentUser) return [];
        return [
            { label: 'Select BD', value: '' },
            ...businessDevelopmentUser.map((val) => ({ label: val.name, value: val._id })),
        ];
    }, [businessDevelopmentUser]);

    const handleSubmit = async (values: any) => {
        try {
            await formHook.submit(values);
            toast.success('Client Added Successfully');
            navigate('/client-installations');
        } catch (err: any) {
            toast.error(err?.data?.message || 'Something went wrong.');
        }
    };

    return (
        <Formik
            initialValues={formHook.initialValues}
            enableReinitialize
            validationSchema={formHook.schema}
            onSubmit={handleSubmit}
        >
            {({ values, setFieldValue }) => {
                return (
                    <Form>
                        {/* Basic Info */}
                        <Card className="mt-4">
                            <Card.Header>Basic Information</Card.Header>
                            <Card.Body>
                                <Label>
                                    Name <RequiredMark />
                                </Label>
                                <FormikInput name="name" type="text" />
                                <HelpText variant="error">
                                    <ErrorMessage name="name" />
                                </HelpText>

                                <div className="mt-4">
                                    <Label>
                                        Business Development <RequiredMark />
                                    </Label>
                                    <div className="flex gap-2">
                                        <FormikSelect name="businessDevelopmentUser" options={BdUserOption} />

                                        {isLoading && <CircularProgress size={14} />}
                                    </div>

                                    <HelpText variant={'error'}>
                                        <ErrorMessage name="businessDevelopmentUser" />
                                    </HelpText>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* POCs */}
                        <Card className="mt-4">
                            <Card.Header>POCs</Card.Header>
                            <Card.Body>
                                <FieldArray name="poc">
                                    {({ remove, push }) => (
                                        <>
                                            {values.poc.map((_, index) => (
                                                <div
                                                    key={index}
                                                    className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-2"
                                                >
                                                    <div className="md:col-span-3">
                                                        <Label>
                                                            Name <RequiredMark />
                                                        </Label>
                                                        <FormikInput name={`poc.${index}.name`} type="text" />
                                                        <HelpText variant={'error'}>
                                                            <ErrorMessage name={`poc.${index}.name`} />
                                                        </HelpText>
                                                    </div>
                                                    <div className="md:col-span-3">
                                                        <Label>
                                                            Email <RequiredMark />
                                                        </Label>
                                                        <FormikInput name={`poc.${index}.email`} type="email" />
                                                        <HelpText variant={'error'}>
                                                            <ErrorMessage name={`poc.${index}.email`} />
                                                        </HelpText>
                                                    </div>
                                                    <div className="md:col-span-3">
                                                        <Label>Phone</Label>
                                                        <FormikInput name={`poc.${index}.phoneNumber`} type="number" />
                                                        <HelpText variant={'error'}>
                                                            <ErrorMessage name={`poc.${index}.phoneNumber`} />
                                                        </HelpText>
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <Label>Designation</Label>
                                                        <FormikInput name={`poc.${index}.designation`} type="text" />
                                                    </div>
                                                    <div className="flex items-end md:items-center md:py-6">
                                                        <Button type="button" size="xs" onClick={() => remove(index)}>
                                                            <DeleteIcon />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                            <Button
                                                className="mt-4"
                                                size="sm"
                                                type="button"
                                                onClick={() =>
                                                    push({
                                                        name: '',
                                                        email: '',
                                                        phoneNumber: '',
                                                        designation: '',
                                                    })
                                                }
                                            >
                                                <PlusIcon /> Add POC
                                            </Button>
                                        </>
                                    )}
                                </FieldArray>
                            </Card.Body>
                        </Card>

                        {/* Location */}
                        <Card className="mt-4">
                            <Card.Header>Location</Card.Header>
                            <Card.Body>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <Label>
                                            Country <RequiredMark />
                                        </Label>
                                        <FormikSelect name="location.country" options={countryOptions} showSearch />
                                        <HelpText variant="error">
                                            <ErrorMessage name="location.country" />
                                        </HelpText>
                                    </div>
                                    <div className="md:col-span-2">
                                        <Label>Google Maps Link</Label>
                                        <FormikInput name="location.googleMapsLink" type="text" />
                                    </div>
                                    <div>
                                        <Label>Latitude</Label>
                                        <FormikInput name="location.latitude" type="text" className="mb-4" />
                                    </div>
                                    <div>
                                        <Label>Longitude</Label>
                                        <FormikInput name="location.longitude" type="text" className="mb-4" />
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Contract */}
                        <Card className="mt-4">
                            <Card.Header>Contract</Card.Header>
                            <Card.Body>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label>Start Date</Label>
                                        <FormikDatePicker name="contract.startDate" isClearable />
                                        <FormHelperText variant={'error'}>
                                            <ErrorMessage name="contract.startDate" />
                                        </FormHelperText>
                                    </div>

                                    <div>
                                        <Label>End Date</Label>
                                        <FormikDatePicker name="contract.endDate" isClearable />
                                        <FormHelperText variant={'error'}>
                                            <ErrorMessage name="contract.endDate" />
                                        </FormHelperText>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* File Upload */}
                        <Card className="mt-4">
                            <Card.Header>Documents</Card.Header>
                            <Card.Body>
                                <Label>Upload Files</Label>
                                <input
                                    type="file"
                                    multiple
                                    className="mt-2"
                                    onChange={(event) => {
                                        const selectedFiles = event.currentTarget.files;
                                        if (selectedFiles) {
                                            const fileArray = Array.from(selectedFiles);
                                            setFieldValue('files', [...values.files, ...fileArray]);
                                        }
                                    }}
                                />

                                <ul className="mt-2 list-disc pl-5 space-y-1">
                                    {values.files?.map((file: File, idx: number) => (
                                        <li key={idx} className="flex items-center gap-2">
                                            <DocFileIcon className="w-7" extension={getFileExtension(file.name)} />
                                            <span className="truncate max-w-xs">{file.name}</span>
                                            <Button
                                                size="xs"
                                                type="button"
                                                className="ml-2"
                                                onClick={() => {
                                                    const updated = [...values.files];
                                                    updated.splice(idx, 1);
                                                    setFieldValue('files', updated);
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            </Card.Body>
                        </Card>

                        <Button type="submit" className="mt-6  w-full sm:w-auto" disabled={formHook.isSubmitting}>
                            {formHook.isSubmitting ? <CircularProgress size={20} color={'inherit'} /> : 'Add Client'}
                        </Button>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default AddClientInstallationForm;
